import { RootState } from "store";
import { toggleAudio, toggleVideo, toggleBackgroundEffect } from "store/reducers/livestreamSettings";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Draggable from "react-draggable";
import { AiOutlineExpandAlt } from "react-icons/ai";
import { HiOutlineVideoCamera, HiOutlineVideoCameraSlash, HiSparkles } from "react-icons/hi2";
import { HiChevronDown, HiOutlineVolumeUp } from "react-icons/hi";
import { IoMicOffOutline, IoMicOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { FiMinimize2 } from "react-icons/fi";
import { AudioControl, VideoControl } from "./controls";
import { User } from "models/User";
import RoomClient from "components/containers/utils/mediaSoup/RoomClient";
import AudioOutputSelector from "./audioOutputSelector";
import { RiSoundModuleLine } from "react-icons/ri";

interface SelfieSegmentationResults {
  segmentationMask: ImageBitmap | HTMLImageElement | HTMLCanvasElement | HTMLVideoElement;
  image: HTMLImageElement | HTMLCanvasElement | HTMLVideoElement;
}

interface SelfieSegmentation {
  setOptions(options: { selfieMode: boolean; modelSelection: number; effect?: string }): void;
  onResults(callback: (results: SelfieSegmentationResults) => void): void;
  send(options: { image: HTMLVideoElement }): Promise<void>;
  close(): void;
}

declare global {
  interface Window {
    SelfieSegmentation: new (options: {
      locateFile: (file: string) => string;
    }) => SelfieSegmentation;
  }
}

interface TraineeProps {
  roomClient: RoomClient | null;
}

const selfieSegmentationScriptUrl = "https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/selfie_segmentation.js";

const TraineeDraggableCard = ({ roomClient }: TraineeProps) => {
  const settings = useSelector((state: RootState) => state.live.settings);
  const auth = useSelector((state: RootState) => state.auth.user as User);
  const authUser = auth.email;
  const dispatch = useDispatch();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const bgImageRef = useRef<HTMLImageElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const mediaPipeVideoRef = useRef<HTMLVideoElement | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const originalStyles = useRef<{ [key: string]: string }>({});
  const selfieSegmentationRef = useRef<SelfieSegmentation | null>(null);
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string>("");
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [isAudioMenuOpen, setIsAudioMenuOpen] = useState<boolean>(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const microphoneRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const [mediaPipeStream, setMediaPipeStream] = useState<MediaStream | null>(null);
  const canvasStreamRef = useRef<MediaStream | null>(null);
  const testHasRunRef = useRef<boolean>(false);
  const isMediaPipeRunningRef = useRef<boolean>(false);
  const isVideoReswitchedRef = useRef<boolean>(false);
  const [micState, setMicState] = useState<'on' | 'off' | 'unsupported'>('unsupported');
  const [webcamState, setWebcamState] = useState<'on' | 'off'>('off');
  const producers = useSelector((state: any) => state.live.settings?.producers);
  const lastBgStateRef = useRef<{ bgImage: string; videoEnabled: boolean }>({
    bgImage: '',
    videoEnabled: false,
  });
  const currentUserBgImageRef = useRef<string>('');

  const videoProducer = useMemo(() => {
    return Object.values(producers).find((p: any) => p.kind === "video") as any;
  }, [producers]);

  const audioProducer = useMemo(() => {
    return Object.values(producers).find((p: any) => p.kind === "audio") as any;
  }, [producers]);

  const currentUserBgImage = useMemo(() => {
    const bgImage = settings.bgImage?.[authUser] || '';
    console.debug(`currentUserBgImage computed for ${authUser}:`, bgImage);
    currentUserBgImageRef.current = bgImage;
    return bgImage;
  }, [settings.bgImage, authUser]);

  useEffect(() => {
    setMicState(
      !audioProducer
        ? 'unsupported'
        : !audioProducer.paused && settings.audio
          ? 'on'
          : 'off'
    );
    setWebcamState(
      videoProducer && videoProducer.type !== 'share' ? 'on' : 'off'
    );
    console.debug('Mic and Webcam state updated:', { micState, webcamState, settingsVideo: settings.video });
  }, [audioProducer, videoProducer, settings.audio]);

  useEffect(() => {
    if (settings.video) {
      isVideoReswitchedRef.current = true;
    }
    console.debug('Video state changed:', { settingsVideo: settings.video, isVideoReswitched: isVideoReswitchedRef.current });
  }, [settings.video]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = selfieSegmentationScriptUrl;
    script.async = true;
    script.onload = () => {
      if (window.SelfieSegmentation) {
        selfieSegmentationRef.current = new window.SelfieSegmentation({
          locateFile: (file: string) =>
            `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`,
        });
        selfieSegmentationRef.current.setOptions({
          selfieMode: true,
          modelSelection: 1,
          effect: "mask",
        });
        selfieSegmentationRef.current.onResults(onResults);
        console.debug('SelfieSegmentation initialized');
      }
    };
    script.onerror = () => console.error("Failed to load MediaPipe SelfieSegmentation script");
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
      if (selfieSegmentationRef.current) {
        selfieSegmentationRef.current.close();
        selfieSegmentationRef.current = null;
      }
      console.debug('SelfieSegmentation cleaned up');
    };
  }, []);

  useEffect(() => {
    if (bgImageRef.current && currentUserBgImage) {
      bgImageRef.current.onerror = () => {
        console.error(`Failed to load background image: ${currentUserBgImage}`);
        dispatch(toggleBackgroundEffect());
      };
      bgImageRef.current.onload = () => {
        console.debug(`Background image loaded successfully: ${currentUserBgImage}`);
      };
    }
  }, [currentUserBgImage, authUser, dispatch]);

  const getMediaPipeStream = async () => {
    try {
      if (mediaPipeStream) {
        mediaPipeStream.getTracks().forEach((track) => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720, facingMode: 'user' },
      });
      setMediaPipeStream(stream);
      if (!mediaPipeVideoRef.current) {
        mediaPipeVideoRef.current = document.createElement("video");
        mediaPipeVideoRef.current.autoplay = true;
        mediaPipeVideoRef.current.playsInline = true;
        mediaPipeVideoRef.current.muted = true;
        mediaPipeVideoRef.current.style.display = "none";
        document.body.appendChild(mediaPipeVideoRef.current);
      }
      mediaPipeVideoRef.current.srcObject = stream;
      await mediaPipeVideoRef.current.play();
      console.debug('MediaPipe stream initialized');
      return stream;
    } catch (error) {
      console.error("getMediaPipeStream() failed:", error);
      throw error;
    }
  };

  const toggleWebCam = () => {
    if (webcamState === 'on') {
      roomClient?.disableWebcam();
      dispatch(toggleVideo(false));
    } else {
      roomClient?.enableWebcam();
      dispatch(toggleVideo(true));
    }
  };

  const toggleMic = () => {
    if (micState === 'on') {
      roomClient?.muteMic();
      dispatch(toggleAudio(false));
    } else {
      roomClient?.unmuteMic();
      dispatch(toggleAudio(true));
    }
  };

  const setCanvasResolution = (canvas: HTMLCanvasElement, resolution: string) => {
    const resolutions: { [key: string]: { width: number; height: number } } = {
      "720p": { width: 1280, height: 720 },
      "1080p": { width: 1920, height: 1080 },
      "2k": { width: 2560, height: 1440 },
      "4k": { width: 3840, height: 2160 },
    };
    const { width, height } = resolutions[resolution] || { width: 1920, height: 1080 };
    canvas.width = width;
    canvas.height = height;
    if (!resolutions[resolution]) console.warn("Unknown resolution, defaulting to 1080p");
  };

  const replaceCanvasTrack = async () => {
    try {
      const canvas = canvasRef.current;
      if (!canvas) throw new Error("Canvas element not found");

      setCanvasResolution(canvas, "720p");

      if (canvasStreamRef.current) {
        canvasStreamRef.current.getTracks().forEach((track) => track.stop());
        canvasStreamRef.current = null;
      }

      const canvasStream = canvas.captureStream(30);
      canvasStreamRef.current = canvasStream;
      const canvasTrack = canvasStream.getVideoTracks()[0];

      if (!canvasTrack) throw new Error("canvasTrack not found");

      if (roomClient && typeof roomClient.replaceVideoStream === "function") {
        let attempt = 0;
        let data = { status: 'no-producer' };

        while (attempt < 3 && data.status === 'no-producer') {
          data = await roomClient.replaceVideoStream(canvasTrack);
          if (data.status === 'success') {
            console.debug("Canvas track sent to client");
            return;
          }
          if (data.status === 'no-producer') {
            attempt++;
            if (attempt < 3) {
              console.debug(`replaceVideoStream retry ${attempt}/3…`);
              await new Promise((r) => setTimeout(r, 1000));
            }
          } else {
            console.warn(`Failed to send canvas track (status: ${data.status})`);
            break;
          }
        }

        if (data.status !== 'success') {
          isMediaPipeRunningRef.current = false;
          console.warn(`Failed to send canvas track (status: ${data.status})`);
        }
      } else {
        isMediaPipeRunningRef.current = false;
        console.warn("roomClient.replaceVideoStream is not available");
      }
    } catch (error) {
      isMediaPipeRunningRef.current = false;
      console.error("Error replacing canvas track:", error);
    }
  };

  const onResults = (results: SelfieSegmentationResults) => {
    const bgImage = currentUserBgImageRef.current;

    if (!bgImage || !settings.video || !isMediaPipeRunningRef.current) {
      return;
    }

    const canvasElem = canvasRef.current;
    const bgImageElement = bgImageRef.current;
    if (!canvasElem || !bgImageElement || !results.image || !results.segmentationMask) {
      return;
    }

    const canvasCtx = canvasElem.getContext("2d");
    if (!canvasCtx) {
      console.error("Failed to get canvas context");
      return;
    }

    canvasCtx.save();
    canvasCtx.clearRect(0, 0, canvasElem.width, canvasElem.height);
    canvasElem.width = results.image.width;
    canvasElem.height = results.image.height;
    canvasCtx.drawImage(results.image, 0, 0, canvasElem.width, canvasElem.height);
    canvasCtx.globalCompositeOperation = "destination-in";
    canvasCtx.drawImage(results.segmentationMask, 0, 0, canvasElem.width, canvasElem.height);
    canvasCtx.globalCompositeOperation = "destination-over";
    if (bgImage === "../bg/1.png") {
      // Instead of replacing with bgImageElement, show original camera background faintly
      canvasCtx.filter = "blur(15px)";  // reduce opacity of original background
      canvasCtx.drawImage(results.image, 0, 0, canvasElem.width, canvasElem.height);
      canvasCtx.filter = "none";
    } else if (bgImageElement) {
      // Normal logic — draw selected background
      canvasCtx.drawImage(bgImageElement, 0, 0, canvasElem.width, canvasElem.height);
    }
    canvasCtx.restore();

    if (!testHasRunRef.current || isVideoReswitchedRef.current) {
      testHasRunRef.current = true;
      isVideoReswitchedRef.current = false;
      replaceCanvasTrack();
    }
  };

  const sendToMediaPipe = async () => {
    const bgImage = currentUserBgImageRef.current;
    if (!isMediaPipeRunningRef.current || !bgImage || !settings.video || !bgImageRef.current?.complete) {
      // console.debug("sendToMediaPipe skipped:", {
      //   isMediaPipeRunning: isMediaPipeRunningRef.current,
      //   currentUserBgImage: bgImage,
      //   settingsVideo: settings.video,
      //   bgImageLoaded: bgImageRef.current?.complete,
      // });
      setTimeout(() => requestAnimationFrame(sendToMediaPipe), 33);
      return;
    }

    const sourceVideo = mediaPipeVideoRef.current;
    if (!sourceVideo || !sourceVideo.videoWidth) {
      // console.debug("sendToMediaPipe: Waiting for video source");
      setTimeout(() => requestAnimationFrame(sendToMediaPipe), 33);
      return;
    }

    try {
      await selfieSegmentationRef.current?.send({ image: sourceVideo });
      setTimeout(() => requestAnimationFrame(sendToMediaPipe), 33);
    } catch (err) {
      console.error("sendToMediaPipe() failed:", err);
      setTimeout(() => requestAnimationFrame(sendToMediaPipe), 33);
    }
  };

  const initializeMediaPipe = async () => {
    const bgImage = currentUserBgImageRef.current;
    if (!bgImage || !settings.video) {
      // console.debug("initializeMediaPipe skipped:", {
      //   currentUserBgImage: bgImage,
      //   settingsVideo: settings.video,
      // });
      return;
    }

    try {
      isMediaPipeRunningRef.current = true;
      await getMediaPipeStream();
      // console.debug('Starting sendToMediaPipe');
      sendToMediaPipe();
    } catch (error) {
      console.error("initializeMediaPipe() failed:", error);
      isMediaPipeRunningRef.current = false;
      setTimeout(initializeMediaPipe, 1000);
    }
  };

  const revertToOriginalWebcam = async () => {
    try {
      if (canvasStreamRef.current) {
        canvasStreamRef.current.getTracks().forEach((track) => track.stop());
        canvasStreamRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      const videoTrack = stream.getVideoTracks()[0];
      if (roomClient && roomClient.replaceVideoStream) {
        await roomClient.replaceVideoStream(videoTrack);
        // console.debug('Reverted to original webcam stream');
      }
      testHasRunRef.current = false;
    } catch (error) {
      console.error("revertToOriginalWebcam() failed:", error);
      setTimeout(revertToOriginalWebcam, 500);
    }
  };

  const cleanupMediaPipe = () => {
    isMediaPipeRunningRef.current = false;
    if (mediaPipeStream) {
      mediaPipeStream.getTracks().forEach((track) => track.stop());
      setMediaPipeStream(null);
    }
    if (mediaPipeVideoRef.current) {
      mediaPipeVideoRef.current.srcObject = null;
      if (mediaPipeVideoRef.current.parentNode) {
        mediaPipeVideoRef.current.parentNode.removeChild(mediaPipeVideoRef.current);
      }
      mediaPipeVideoRef.current = null;
    }
    if (canvasStreamRef.current) {
      canvasStreamRef.current.getTracks().forEach((track) => track.stop());
      canvasStreamRef.current = null;
    }
    testHasRunRef.current = false;
    console.debug('MediaPipe resources cleaned up');
  };

  useEffect(() => {
    const currentBgImage = currentUserBgImageRef.current;
    const videoEnabled = settings.video;

    // console.debug(`Background state check for ${authUser}:`, {
    //   currentBgImage,
    //   videoEnabled,
    //   isVideoReswitched: isVideoReswitchedRef.current,
    //   lastBgState: lastBgStateRef.current,
    // });

    if (
      currentBgImage === lastBgStateRef.current.bgImage &&
      videoEnabled === lastBgStateRef.current.videoEnabled &&
      !isVideoReswitchedRef.current
    ) {
      console.debug('No background state change, skipping');
      return;
    }

    if (videoEnabled && currentBgImage) {
      console.debug(`Initializing MediaPipe for ${authUser} with bgImage: ${currentBgImage}`);
      initializeMediaPipe();
    } else {
      console.debug(`Cleaning up MediaPipe for ${authUser}:`, { videoEnabled, currentBgImage });
      cleanupMediaPipe();
      if (videoEnabled) {
        revertToOriginalWebcam();
      }
    }

    lastBgStateRef.current = { bgImage: currentBgImage, videoEnabled };
  }, [currentUserBgImage, settings.video, authUser]);

  // Handle video stream from producers
  useLayoutEffect(() => {
    const videoEl = videoRef.current;

    if (
      !videoEl ||
      !videoProducer?.track ||
      !(videoProducer.track instanceof MediaStreamTrack)
    ) {
      console.debug("No video element or valid track");
      return;
    }

    const stream = new MediaStream([videoProducer.track.clone()]);

    const playVideo = async () => {
      try {
        if (videoEl.readyState < 2) {
          await new Promise<void>((resolve) => {
            videoEl.onloadedmetadata = () => resolve();
          });
        }
        await videoEl.play();
        console.debug("Video play success");
      } catch (err) {
        console.error("Video play error:", err);
        if (err.name === "AbortError") {
          console.debug("Ignoring AbortError on play");
        } else {
          throw err;
        }
      }
    };

    if (videoEl.srcObject) {
      const oldStream = videoEl.srcObject as MediaStream;
      oldStream.getTracks().forEach((track) => track.stop());
      videoEl.srcObject = null;
    }

    videoEl.srcObject = stream;
    playVideo();

    return () => {
      videoEl.srcObject = null;
      stream.getTracks().forEach((track) => track.stop());
      videoEl.onloadedmetadata = null;
    };
  }, [videoProducer?.track]);

  // Fetch audio devices
  useEffect(() => {
    const fetchAudioDevices = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioOutputs = devices.filter((device) => device.kind === "audiooutput");
        setAudioOutputDevices(audioOutputs);
        if (audioOutputs.length > 0) {
          const bluetoothDevice = audioOutputs.find(
            (device) =>
              device.label.toLowerCase().includes("bluetooth") ||
              device.label.toLowerCase().includes("wireless") ||
              device.label.toLowerCase().includes("headset")
          );
          const selectedDeviceId = bluetoothDevice ? bluetoothDevice.deviceId : audioOutputs[0].deviceId;
          setSelectedAudioOutput(selectedDeviceId);
          if (videoRef.current) {
            // @ts-ignore
            await videoRef.current.setSinkId(selectedDeviceId).catch((err) =>
              console.error("Error setting audio output:", err)
            );
          }
        }
      } catch (error) {
        console.error("Error fetching audio devices:", error);
      }
    };
    fetchAudioDevices();
    const handleDeviceChange = () => fetchAudioDevices();
    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    return () => navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
  }, []);

  const handleMaximize = () => {
    setIsMaximized((value) => !value);
    if (containerRef.current) {
      if (!isMaximized) {
        originalStyles.current = {
          position: containerRef.current.style.position,
          top: containerRef.current.style.top,
          left: containerRef.current.style.left,
          transform: containerRef.current.style.transform,
          width: containerRef.current.style.width,
          height: containerRef.current.style.height,
          zIndex: containerRef.current.style.zIndex,
          backgroundColor: containerRef.current.style.backgroundColor,
        };
        Object.assign(containerRef.current.style, {
          position: "fixed",
          top: "0",
          left: "50%",
          transform: "translateX(-50%)",
          width: "100vw",
          height: "100vh",
          zIndex: "9999",
          backgroundColor: "black",
        });
      } else {
        Object.assign(containerRef.current.style, originalStyles.current);
      }
    }
  };
  const handleMinimize = () => {
    setIsMinimized((value) => !value);
    if (containerRef.current && isMinimized) {
      Object.assign(containerRef.current.style, originalStyles.current);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsAudioMenuOpen(false);
      }
    };
    if (isAudioMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isAudioMenuOpen]);

  const selectedAudioDevice = useSelector((state: RootState) => state.live.settings.selectedAudioDevice);
  const selectedMicDevice = useSelector((state: RootState) => state.live.settings.selectedMicDevice);
  const prevAudioDeviceRef = useRef(selectedAudioDevice);
  const prevMicDeviceRef = useRef(selectedMicDevice);

  useEffect(() => {
    if (selectedAudioDevice) {
      setTimeout(() => applyAudioOutputDevice(selectedAudioDevice), 1000);
    }
  }, []);

  useEffect(() => {
    const prevAudioDevice = prevAudioDeviceRef.current;
    const prevMicDevice = prevMicDeviceRef.current;

    if (selectedAudioDevice && selectedAudioDevice !== prevAudioDevice) {
      console.debug("PeerView: Audio output device changed:", {
        from: prevAudioDevice,
        to: selectedAudioDevice,
      });
      applyAudioOutputDevice(selectedAudioDevice);
      const retryTimer = setTimeout(() => {
        console.debug("PeerView: Retrying audio output device application");
        applyAudioOutputDevice(selectedAudioDevice);
      }, 500);
      prevAudioDeviceRef.current = selectedAudioDevice;
      return () => clearTimeout(retryTimer);
    }

    if (selectedMicDevice && selectedMicDevice !== prevMicDevice) {
      console.debug("PeerView: Microphone device changed:", {
        from: prevMicDevice,
        to: selectedMicDevice,
      });
      reinitializeMicrophone();
      prevMicDeviceRef.current = selectedMicDevice;
    }
  }, [selectedAudioDevice, selectedMicDevice]);

  const applyAudioOutputDevice = async (deviceId: string) => {
    if (!deviceId) {
      console.debug("PeerView: No device ID provided for audio output");
      return;
    }

    console.debug("PeerView: Applying audio output device:", deviceId);
    const audioElements: any = document.querySelectorAll("audio");
    let successCount = 0;

    for (const audioElem of audioElements) {
      if (!audioElem || typeof audioElem.setSinkId !== "function") continue;

      try {
        const currentSinkId = audioElem.sinkId || "";
        if (currentSinkId === deviceId) {
          successCount++;
          continue;
        }

        await audioElem.setSinkId(deviceId);
        if (audioElem.sinkId === deviceId) successCount++;
        else console.warn(`PeerView: sinkId may not have been set correctly. Expected: ${deviceId}, Got: ${audioElem.sinkId}`);
      } catch (error) {
        console.error("PeerView: Failed to set sinkId:", error);
      }
    }

    console.debug(`PeerView: Applied audio device to ${successCount}/${audioElements.length} audio elements`);
  };

  const reinitializeMicrophone = async () => {
    try {
      if (roomClient && typeof roomClient.reinitializeMicrophone === "function") {
        await roomClient.reinitializeMicrophone();
        console.debug("Microphone reinitialized");
      }
    } catch (error) {
      console.error("Failed to reinitialize microphone:", error);
    }
  };

  return (
    <Draggable>
      <div
        ref={containerRef}
        className={`fixed bottom-16 right-10 flex flex-col items-center gap-2 z-50 ${isMinimized ? 'w-32 h-12' : ''
          }`}
      >
        {/* Always-rendered preview container; resizes via class */}
        <div
          className={`relative rounded-lg overflow-hidden shadow-lg flex items-center justify-between px-2 ${isMinimized
              ? 'h-12 w-32 bg-gradient-to-r from-gray-800 to-gray-600 cursor-pointer'
              : 'h-32 w-56'
            }`}
          onClick={isMinimized ? handleMinimize : undefined}
          onTouchStart={isMinimized ? handleMinimize : undefined}
        >
          {currentUserBgImage && settings.video ? (
            <img
              ref={bgImageRef}
              src={currentUserBgImage}
              id="bg_image_id"
              style={{ height: '100%', width: '100%', objectFit: 'cover' }}
              hidden
            />
          ) : null}

          {/* Absolute fill layer for preview content; always in DOM */}
          <div className="absolute inset-0 flex justify-center items-center">
            {/* Video/canvas always mounted if video enabled; hidden via visibility when minimized */}
            {settings.video && (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="absolute inset-0 h-32 w-56 object-cover"
                  hidden={currentUserBgImage && settings.video}
                  style={{
                    visibility: isMinimized ? 'hidden' : 'visible',
                    objectPosition:
                      window.innerHeight > window.innerWidth // portrait device
                        ? 'center 50%' // shift down to show full face
                        : 'center center', // normal on landscape
                  }}
                />
                {currentUserBgImage && settings.video ? (
                  <canvas
                    ref={canvasRef}
                    className="absolute inset-0 h-32 w-56 object-cover"
                    style={{
                      visibility: isMinimized ? 'hidden' : 'visible',
                      objectPosition:
                        window.innerHeight > window.innerWidth
                          ? 'center 50%'
                          : 'center center',
                    }}
                    id="newCanvas"
                  />
                ) : null}
              </>
            )}


            {/* No-video initials (only show when not minimized) */}
            {!isMinimized && !settings.video && auth && (
              <div className="w-full h-full bg-black flex justify-center items-center">
                <span className="w-20 flex justify-center items-center h-20 rounded-full bg-white text-lg font-bold text-app-blue">
                  {auth?.firstName.charAt(0) + auth?.lastName.charAt(0)}
                </span>
              </div>
            )}
          </div>

          {/* Minimize overlay: covers preview with +/initials; click + to toggle */}
          {isMinimized && (
            <div
              className="absolute inset-0 flex items-center justify-between w-full text-white p-4"
              style={{ pointerEvents: 'auto' }} // Allow clicks through
            >
              <span
                className="flex items-center justify-center w-6 h-6  text-xs font-bold cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMinimize();
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  handleMinimize();
                }}
              >
                Max
              </span>
              <span className="text-lg font-bold">
                {auth?.firstName.charAt(0) + auth?.lastName.charAt(0)}
              </span>
            </div>
          )}

          {/* Bottom bar: only when not minimized */}
          {!isMinimized && (
            <div className="flex justify-between items-center absolute bottom-0 left-0 right-0 bg-gray-800 opacity-75 w-full px-2 py-1 text-white text-xs">
              <button className="" onClick={toggleWebCam} onTouchStart={toggleWebCam}>
                {webcamState === 'on' || settings.video ? (
                  <HiOutlineVideoCamera />
                ) : (
                  <HiOutlineVideoCameraSlash className="text-red-500" />
                )}
              </button>
              <span className="truncate">{auth?.firstName + ' ' + auth?.lastName}</span>
              <button className="" onClick={toggleMic} onTouchStart={toggleMic}>
                {settings.audio ? <IoMicOutline /> : <IoMicOffOutline className="text-red-500" />}
              </button>
              <button
                className=""
                onClick={handleMinimize}
                onTouchStart={handleMinimize}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <span className="text-white text-xs">Min</span>
              </button>
            </div>
          )}
        </div>

        {/* Lower controls: only when not minimized */}
        {!isMinimized && (
          <div className="flex gap-3 mobile:hidden">
            <VideoControl roomClient={roomClient} />
            {settings.video ? (
              <div className="bg-app-yellow cursor-pointer hover:scale-110 ease-in-out delay-75 w-14 h-14 rounded-2xl flex justify-center items-center">
                <HiSparkles
                  color="black"
                  size={24}
                  onClick={() => dispatch(toggleBackgroundEffect())}
                />
              </div>
            ) : null}
            <AudioControl roomClient={roomClient} />
           {settings.audio && (
              <div className="flex flex-col items-center gap-2 relative">
                <div
                  ref={buttonRef}
                  className="audio-output-selector bg-app-yellow cursor-pointer hover:scale-110 ease-in-out delay-75 w-14 h-14 rounded-2xl flex justify-center items-center"
                  style={{ position: "relative" }}
                  onClick={() => setIsAudioMenuOpen((prev) => !prev)}
                >
                  <RiSoundModuleLine style={{ fontSize: "24px", color: "black" }} />
                </div>
                {isAudioMenuOpen && (
                  <AudioOutputSelector
                    showTrigger={false}
                    open={isAudioMenuOpen}
                    onOpenChange={setIsAudioMenuOpen}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Draggable>
  );
};

export default TraineeDraggableCard;